import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useFetchClasses from "../../hooks/useFetchClasses";
import { ApiUrl } from "../../../ApiUrl";

const TransferCertificateForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const studentData = location.state || {};
  const { classes } = useFetchClasses();
  const certificateData = studentData.studentcertificatedetails || {};
  const [formData, setFormData] = useState({
    ...studentData,
    ...certificateData,
  });

  const updateField = (name, value) => {
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const normalizeSubjectsStudied = (value) => {
    if (value === null || value === undefined) return "";
    if (Array.isArray(value)) {
      return value
        .map((subject) => normalizeSubjectsStudied(subject))
        .filter(Boolean)
        .join(", ");
    }
    if (typeof value === "object") {
      return normalizeSubjectsStudied(
        value.subject_name ||
          value.subjectcode ||
          value.subject_code ||
          value.name ||
          value.label ||
          ""
      );
    }
    return String(value);
  };

  const formatDateValue = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toISOString().split("T")[0];
  };

  const dateOfAdmission = formatDateValue(
    formData.date_of_admission || formData.admission_date || formData.admissionDate
  );

  const clearEditableFields = () => {
    setFormData((previous) => ({
      ...previous,
      document_no: "",
      qualified_for_promotion: "",
      reason_for_tc: "",
      general_conduct: "",
      tc_applied_date: "",
      tc_issued_date: "",
    }));
  };

  const handleSave = async () => {
    try {
      const student =
        localStorage.getItem("selectedCertificateStudentId") ||
        localStorage.getItem("studentId") ||
        formData.studentId ||
        formData.student ||
        "";
      const session = localStorage.getItem("academicSessionId");
      const org_id = localStorage.getItem("orgId");
      const branch_id = localStorage.getItem("branchId");
      const document_type =
        localStorage.getItem("selectedDocumentType") ||
        localStorage.getItem("documentType") ||
        localStorage.getItem("document_type") ||
        "TC";
      const transferCertificateId = Number(
        localStorage.getItem("transfer_certificate_id") || 0
      );
      const [prefix, ...postfixParts] = (formData.document_no || "").split("/");
      const postfix = postfixParts.join("/");

      if (!prefix || !postfix) {
        alert("Please provide a valid Ref No. in the format 'prefix/postfix'.");
        return;
      }
      const missingFields = [];
      if (!formData.tc_applied_date) {
        missingFields.push("Date of Application");
      }
      if (!formData.reason_for_tc?.trim()) {
        missingFields.push("Reason for Leaving");
      }
      if (missingFields.length > 0) {
        alert(`Please provide: ${missingFields.join(" and ")}.`);
        return;
      }

      const payload = {
        student,
        session,
        org_id,
        branch_id,
        document_type,
        transfer_certificate_no_prefix: prefix,
        transfer_certificate_no_postfix: postfix,
        transfer_certificate_no: formData.transfer_certificate_no || "4",
        transfer_certificate_id: transferCertificateId,
        tc_applied_date: formData.tc_applied_date,
        tc_issued_date: formData.tc_issued_date || null,
        school_board_last_taken: formData.school_board_last_taken || "",
        whether_failed: formData.whether_failed || "",
        subjects_studied: normalizeSubjectsStudied(formData.subjects_studied),
        qualified_for_promotion: formData.qualified_for_promotion || "",
        month_fee_paid: formData.month_fee_paid || "",
        fee_concession_availed: formData.fee_concession_availed || "",
        total_no_working_days: formData.total_no_working_days || "",
        total_no_working_days_present: formData.total_no_working_days_present || "",
        ncc_cadet_details: formData.ncc_cadet_details || "",
        games_played_details: formData.games_played_details || "",
        general_conduct: formData.general_conduct || "",
        other_remarks: formData.other_remarks || "",
        status: formData.status || "N",
        cancelled_on: formData.cancelled_on || null,
        cancelled_remarks: formData.cancelled_remarks || "",
        cancelled_by: formData.cancelled_by || "",
        rollno: formData.rollno || "",
        cultural_activities: formData.cultural_activities || "",
        other_activities: formData.other_activities || "",
        marks_obtained: formData.marks_obtained || "",
        from_month: formData.from_month || "",
        to_month: formData.to_month || "",
        class_last_studied: formData.classId || "",
      };

      const requestUrl = transferCertificateId
        ? `${ApiUrl.apiurl}transfer-certificate/update/${session}/${branch_id}/${org_id}/${transferCertificateId}/`
        : `${ApiUrl.apiurl}transfer-certificate/`;
      const response = await fetch(requestUrl, {
        method: transferCertificateId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Unable to save Transfer Certificate.");
      }

      alert(
        transferCertificateId
          ? "Transfer Certificate updated successfully!"
          : "Transfer Certificate saved successfully!"
      );
      navigate("/admin/student-certificate", {
        state: { refreshCertificates: Date.now() },
      });
    } catch (error) {
      console.error("Error saving Transfer Certificate:", error);
      alert(error.message || "An error occurred while saving the certificate.");
    }
  };

  const renderField = (label, name, options = {}) => (
    <div className="row align-items-start mb-2" key={name}>
      <div className="col-12 col-md-5 d-flex">
        <span style={{ width: "18px" }}>•</span>
        <label htmlFor={name} style={{ marginBottom: 0 }}>{label}</label>
      </div>
      <div className="col-1 d-none d-md-block">:</div>
      <div className="col-12 col-md-6">
        {options.select ? (
          <select
            id={name}
            className="form-select certificate-field"
            value={formData[name] || ""}
            style={{
              border: "0",
              borderBottom: "2px solid #333",
              borderRadius: 0,
              backgroundColor: "transparent",
              boxShadow: "none",
              paddingLeft: 0,
            }}
            onChange={(event) => updateField(name, event.target.value)}
          >
            <option value="">Select Class</option>
            {classes.map((classItem) => (
              <option key={classItem.id} value={classItem.id}>
                {classItem.classname}
              </option>
            ))}
          </select>
        ) : (
          <input
            id={name}
            type={options.type || "text"}
            className="form-control certificate-field"
            value={formData[name] || ""}
            readOnly={options.readOnly}
            style={{
              border: "0",
              borderBottom: "2px solid #333",
              borderRadius: 0,
              background: "transparent",
              boxShadow: "none",
              paddingLeft: 0,
              paddingRight: 0,
            }}
            onChange={(event) => updateField(name, event.target.value)}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className="container-fluid" style={{ background: "#dfe9f8", minHeight: "100vh", padding: "24px 12px" }}>
      <div className="d-flex gap-2 mb-3">
        <button type="button" className="btn btn-primary" onClick={handleSave}>Save</button>
        <button type="button" className="btn btn-secondary" onClick={clearEditableFields}>Clear</button>
        <button type="button" className="btn btn-danger" onClick={() => navigate("/admin/student-certificate")}>Close</button>
      </div>

      <div style={{ maxWidth: "820px", margin: "0 auto", background: "#fff", border: "1px solid #1d1d1d", padding: "24px 34px 30px", position: "relative", boxShadow: "0 12px 30px rgba(0, 0, 0, 0.12)" }}>
        <div style={{ position: "absolute", inset: "10px", border: "1px solid #1d1d1d", pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div className="d-flex justify-content-between align-items-center mb-3" style={{ fontSize: "14px", fontWeight: 600 }}>
            <div>Ref No - <input aria-label="Reference number" className="certificate-line-field" value={formData.document_no || ""} onChange={(event) => updateField("document_no", event.target.value)} placeholder="TC001" style={{ border: 0, borderBottom: "2px solid #333", borderRadius: 0, outline: 0 }} /></div>
            <div>Date - <input aria-label="Certificate date" className="certificate-line-field certificate-date-field" type="date" value={formatDateValue(formData.tc_issued_date)} onChange={(event) => updateField("tc_issued_date", event.target.value)} style={{ border: 0, borderBottom: "2px solid #333", borderRadius: 0, outline: 0 }} /></div>
          </div>
          <h3 className="text-center" style={{ fontSize: "22px", fontWeight: 700, margin: "22px 0" }}>TRANSFER CERTIFICATE</h3>
          <p className="text-center" style={{ marginBottom: "24px", fontSize: "14px" }}>
            This is to certify that <span className="certificate-inline-value">{formData.studentname || "-"}</span> was a bonafide student of this institution.
          </p>

          {renderField("Name of the student", "studentname", { readOnly: true })}
          {renderField("Name of the father/Husband", "father_name", { readOnly: true })}
          {renderField("Name of the Mother", "mother_name", { readOnly: true })}
          {renderField("Nationality", "nationality", { readOnly: true })}
          {renderField("Religion and Caste", "category", { readOnly: true })}
          {renderField("Date of Birth", "dob", { type: "date", readOnly: true })}
          {renderField("Permanent Address", "resident_address")}
          {renderField("Registration Number", "registration_no")}
          <div className="row align-items-start mb-2">
            <div className="col-12 col-md-5 d-flex">
              <span style={{ width: "18px" }}>•</span>
              <label htmlFor="date_of_admission" style={{ marginBottom: 0 }}>Date of Admission</label>
            </div>
            <div className="col-1 d-none d-md-block">:</div>
            <div className="col-12 col-md-6">
              <input
                id="date_of_admission"
                type="date"
                className="form-control certificate-field"
                value={dateOfAdmission}
                readOnly
                style={{
                  border: "0",
                  borderBottom: "2px solid #333",
                  borderRadius: 0,
                  background: "transparent",
                  boxShadow: "none",
                  paddingLeft: 0,
                  paddingRight: 0,
                }}
              />
            </div>
          </div>
          {renderField("Class in which the student was studying at the time of leaving the college", "classId", { select: true })}
          {renderField("Whether qualified for promotion to higher class", "qualified_for_promotion")}
          {renderField("Date of Application for Certificate", "tc_applied_date", { type: "date" })}
          {renderField("Reason for Leaving the School", "reason_for_tc")}
          {renderField("Conduct and character during the period of study", "general_conduct")}
          {renderField("Date of leaving from college", "tc_issued_date", { type: "date" })}

          <div className="text-end" style={{ marginTop: "42px", fontStyle: "italic", fontWeight: 500 }}>Principal</div>
        </div>
      </div>
    </div>
  );
};

export default TransferCertificateForm;
